import { useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { MdSend } from "react-icons/md";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import CommentRichTextArea from "./CommentRichTextArea";
import RichTextRenderer from "../../common/RichTextRenderer";

// Define types for props and comment structure
type Comment = {
  comment_id: number;
  user_id: number | string;
  username: string;
  pfp?: string;
  comment: string;
  createdAt: string;
  updatedAt: string;
  role?: number; // Add role information
};

// Role mapping function using the predefined mapping
const getRoleDisplayName = (role: UserRole | number | undefined): string => {
  const roleMapping: Partial<Record<UserRole, string>> = {
    [UserRole.BUSINESS_MANAGER]: "Program Manager",
    [UserRole.DEVELOPER]: "Security Engineer",
    [UserRole.BUSINESS_ADMINISTRATOR]: "Organization Admin",
    [UserRole.ADMIN_MANAGER]: "Admin Manager",
    [UserRole.QA]: "QA",
    [UserRole.SUB_ADMIN]: "Sub Admin",
    [UserRole.BUSINESS]: "Business Owner",
    [UserRole.ADMIN]: "Admin",
    [UserRole.RESEARCHER]: "Researcher"
  };
  
  return role ? (roleMapping[role as UserRole] || "User") : "User";
};

// Role color mapping function
const getRoleColor = (role: UserRole | number | undefined): string => {
  const colorMap: Partial<Record<UserRole, string>> = {
    [UserRole.BUSINESS_MANAGER]: "bg-blue-100 text-blue-800",
    [UserRole.DEVELOPER]: "bg-purple-100 text-purple-800",
    [UserRole.BUSINESS_ADMINISTRATOR]: "bg-indigo-100 text-indigo-800",
    [UserRole.ADMIN_MANAGER]: "bg-red-100 text-red-800",
    [UserRole.QA]: "bg-yellow-100 text-yellow-800",
    [UserRole.SUB_ADMIN]: "bg-orange-100 text-orange-800",
    [UserRole.BUSINESS]: "bg-green-100 text-green-800",
    [UserRole.ADMIN]: "bg-gray-100 text-gray-800",
    [UserRole.RESEARCHER]: "bg-cyan-100 text-cyan-800"
  };
  
  return role ? (colorMap[role as UserRole] || "bg-gray-100 text-gray-800") : "bg-gray-100 text-gray-800";
};

type CommentBoxProps = {
  commentList: Comment[];
  reportId?: number | string; // Allow undefined
  userId?: number | string; // Allow undefined
  username?: string; // Allow undefined
  currentUserRole?: UserRole | number; // Add current user role for proper role display
  onSubmitComment: (reportId: number, comment: string) => void; // Match addNewComment signature
  onDeleteComment: (commentId: number) => void;
  onEditComment: (commentId: number, comment: string) => void;
};

const CommentBox = ({
  commentList,
  reportId,
  userId,
  username,
  currentUserRole,
  onSubmitComment,
  onDeleteComment,
  onEditComment
}: CommentBoxProps) => {
  const [newComment, setNewComment] = useState<string>("");
  const [editMode, setEditMode] = useState(false);
  const [currentComment, setCurrentComment] = useState<{
    commentId: number;
    comment: string;
    index: number;
  }>({ commentId: 0, comment: "", index: 0 });

  const methods = useForm();
  const location = useLocation();
  const [highlightedId, setHighlightedId] = useState<number | null>(null);

  // Highlight specific comment on load if hash is present
  useEffect(() => {
    if (location.hash) {
      const commentIdMatch = location.hash.match(/^#comment-(\d+)$/);
      if (commentIdMatch) {
        const commentId = parseInt(commentIdMatch[1], 10);
        setHighlightedId(commentId);
        
        // Wait slightly to ensure rendering, then scroll
        setTimeout(() => {
          const element = document.getElementById(`comment-${commentId}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 550);

        // Remove highlight effect after 4 seconds
        const timer = setTimeout(() => {
          setHighlightedId(null);
        }, 4000);
        return () => clearTimeout(timer);
      }
    }
  }, [commentList, location.hash]);

  // Function to get the role for a comment
  const getCommentRole = (comment: Comment) => {
    const isCurrentUser = String(comment.user_id) === String(userId);
    
    // For current user's comments, use their role
    if (isCurrentUser && currentUserRole !== undefined) {
      return currentUserRole;
    }
    
    // For other users, use the role from the API response
    if (comment.role !== undefined) {
      return comment.role;
    }
    
    // Fallback: no role available
    return undefined;
  };

  const handleCommentSubmit = () => {
    if (newComment.trim() === "" || !reportId) return; // Guard against undefined reportId
    onSubmitComment(Number(reportId), newComment); // Convert reportId to number
    setNewComment("");
    methods.reset();
  };

  const handleCommentDelete = (commentId: number) => {
    onDeleteComment(commentId);
  };

  const handleCommentEdit = (comment: Comment, index: number) => {
    setCurrentComment({
      commentId: comment.comment_id,
      comment: comment.comment,
      index
    });
    setEditMode(true);
  };

  const handleEditedCommentSubmit = () => {
    if (currentComment.comment.trim() === "") return;
    onEditComment(currentComment.commentId, currentComment.comment);
    setEditMode(false);
    setCurrentComment({ commentId: 0, comment: "", index: 0 });
    methods.reset();
  };

  return (
    <FormProvider {...methods}>
      <div className="flex flex-col h-[500px] min-w-0">
        {/* Comment List - Scrollable after 4-5 comments */}
        <div className={`flex-1 ${commentList.length > 4 ? 'overflow-y-auto pr-3' : ''} space-y-4 pb-4 min-w-0 overflow-x-hidden`}>
          {commentList.length < 1 ? (
            <div className="text-gray-500">No Comments Yet...</div>
          ) : (
            commentList.map((comment, idx) => (
              <div
                key={comment.comment_id || idx}
                id={`comment-${comment.comment_id}`}
                className="flex items-start gap-4 min-w-0 transition-all duration-500 w-full"
                style={{
                  backgroundColor: Number(highlightedId) === Number(comment.comment_id) ? '#FEF3C7' : 'transparent',
                  padding: Number(highlightedId) === Number(comment.comment_id) ? '12px' : '0px',
                  borderRadius: Number(highlightedId) === Number(comment.comment_id) ? '8px' : '0px'
                }}
              >
                {/* Profile Picture */}
                <div>
                  {comment.pfp ? (
                    <img
                      src={comment.pfp}
                      alt="Profile"
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-gray-600 text-sm font-medium">
                        {comment.username?.charAt(0) || "?"}
                      </span>
                    </div>
                  )}
                </div>
                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    {/* Username and Role */}
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {comment.username || "Unknown User"}
                      </span>
                      {/* Role Tag */}
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(getCommentRole(comment))}`}>
                        {(() => {
                          const role = getCommentRole(comment);
                          const displayName = getRoleDisplayName(role);
                          return displayName;
                        })()}
                      </span>
                      <span className="text-xs text-gray-500">published</span>
                    </div>
                    {/* Timestamp */}
                    <div className="text-xs text-gray-500">
                      {comment.comment.includes("[Deleted]")
                        ? `Deleted At: ${comment.updatedAt}`
                        : comment.createdAt}
                    </div>
                  </div>
                  {/* Comment Text */}
                  <div className="text-gray-800 min-w-0 w-full">
                    {comment.comment.includes("[Deleted]") ? (
                      <span className="italic text-gray-400 break-words">
                        {comment.comment}
                      </span>
                    ) : (
                      <RichTextRenderer 
                        content={comment.comment} 
                        className="text-sm w-full" 
                      />
                    )}
                  </div>
                  {/* Edit/Delete Buttons */}
                  {comment.user_id === userId &&
                    !comment.comment.includes("[Deleted]") && (
                      <div className="mt-2 flex gap-2 text-sm">
                        <button
                          className="text-blue-500 hover:text-blue-700"
                          onClick={() => handleCommentEdit(comment, idx)}
                        >
                          Edit
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700"
                          onClick={() =>
                            handleCommentDelete(comment.comment_id)
                          }
                        >
                          Delete
                        </button>
                      </div>
                    )}
                </div>
              </div>
            ))
          )}
        </div>
        {/* Comment Input - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-gray-200 pt-4">
          {editMode ? (
            <div className="flex w-full flex-col gap-4">
              <CommentRichTextArea
                name="editComment"
                rules={{ required: "Comment cannot be empty" }}
                value={currentComment.comment}
                onChange={value =>
                  setCurrentComment(prev => ({ ...prev, comment: value }))
                }
              />
              <div className="flex gap-2">
                <button
                  className="rounded-md bg-gray-500 px-4 py-2 text-white hover:bg-gray-600 transition-colors"
                  onClick={() => {
                    setEditMode(false);
                    setCurrentComment({ commentId: 0, comment: "", index: 0 });
                    methods.reset();
                  }}
                >
                  Discard
                </button>
                <button
                  className="rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 transition-colors"
                  onClick={handleEditedCommentSubmit}
                >
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="relative w-full">
              <CommentRichTextArea
                name="newComment"
                rules={{ required: "Comment cannot be empty" }}
                value={newComment}
                onChange={value => setNewComment(value)}
              />
              <button
                className="absolute bottom-2 right-2 flex h-10 w-10 items-center justify-center rounded-md p-3 text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backgroundColor: '#010D2C',
                  color: '#58CC02'
                }}
                onClick={handleCommentSubmit}
                disabled={!reportId || !userId}
              >
                <MdSend />
              </button>
            </div>
          )}
        </div>
      </div>
    </FormProvider>
  );
};

export default CommentBox;
